import { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#1565C0', '#FF8F00', '#2E7D32', '#D32F2F', '#6A1B9A', '#00838F', '#E65100', '#283593', '#00897B', '#F57C00', '#4527A0', '#00ACC1', '#558B2F', '#C62828', '#4E342E'];

function computeHourlyData(tickets) {
  const buckets = {};
  for (let h = 0; h < 24; h++) buckets[h] = 0;
  tickets.forEach(t => {
    if (t.created_at) {
      const h = new Date(t.created_at).getHours();
      buckets[h] = (buckets[h] || 0) + 1;
    }
  });
  return Object.entries(buckets).map(([hour, count]) => ({
    hour: `${hour.padStart(2, '0')}:00`,
    hourNum: +hour,
    count,
  }));
}

function computeServiceData(tickets) {
  const map = {};
  tickets.forEach(t => {
    const name = t.service_leaf || 'Unknown';
    map[name] = (map[name] || 0) + 1;
  });
  const entries = Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const total = entries.reduce((s, d) => s + d.value, 0);
  const threshold = total * 0.03;
  const main = [];
  let other = 0;
  for (const d of entries) {
    if (d.value >= threshold) main.push(d);
    else other += d.value;
  }
  if (other > 0) main.push({ name: 'Other', value: other });
  return main;
}

function computeCounterData(tickets, counters) {
  const map = {};
  const counterNames = {};
  counters.forEach(c => { counterNames[c.id] = c.name; });
  tickets.forEach(t => {
    const name = (t.counter_id && counterNames[t.counter_id]) || t.counter_name || 'Unassigned';
    map[name] = (map[name] || 0) + 1;
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function computeAvgWaitData(tickets, counters) {
  const map = {};
  const counterNames = {};
  counters.forEach(c => { counterNames[c.id] = c.name; });
  tickets.forEach(t => {
    if (t.wait_time_secs != null) {
      const name = (t.counter_id && counterNames[t.counter_id]) || t.counter_name || 'Unassigned';
      if (!map[name]) map[name] = { total: 0, count: 0 };
      map[name].total += t.wait_time_secs;
      map[name].count += 1;
    }
  });
  return Object.entries(map)
    .map(([name, d]) => ({ name, avgWait: Math.round(d.total / d.count) }))
    .sort((a, b) => b.avgWait - a.avgWait);
}

export function ReportVisuals({ tickets, counters }) {
  const hourly = useMemo(() => computeHourlyData(tickets), [tickets]);
  const serviceData = useMemo(() => computeServiceData(tickets), [tickets]);
  const counterData = useMemo(() => computeCounterData(tickets, counters), [tickets, counters]);
  const avgWaitData = useMemo(() => computeAvgWaitData(tickets, counters), [tickets, counters]);

  const peakHour = hourly.reduce((a, b) => a.count > b.count ? a : b, { count: 0 });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-100)', padding: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 4 }}>Tickets by Service</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 12, marginBottom: 12 }}>{serviceData.length} service types</p>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={serviceData} dataKey="value" nameKey="name" cx="38%" cy="50%" outerRadius={95}>
                {serviceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12, color: 'var(--navy)' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-100)', padding: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 4 }}>Peak Hours — Traffic by Time of Day</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 12, marginBottom: 12 }}>Peak: <strong>{peakHour.hour}</strong> ({peakHour.count} tickets)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="Tickets" radius={[4, 4, 0, 0]}>
                {hourly.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-100)', padding: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 4 }}>Tickets Served by Counter</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 12, marginBottom: 12 }}>{counterData.length} counters active</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={counterData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="value" name="Tickets" radius={[0, 4, 4, 0]}>
                {counterData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-100)', padding: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 4 }}>Average Wait Time by Counter</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 12, marginBottom: 12 }}>In seconds</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={avgWaitData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip formatter={v => `${Math.floor(v / 60)}m ${v % 60}s`} />
              <Bar dataKey="avgWait" name="Avg Wait" radius={[0, 4, 4, 0]}>
                {avgWaitData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
